import React, { forwardRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Tooltip, Button, CircularProgress } from '@material-ui/core'
import { useSnackbar } from 'notistack';

/**
 * 
 * A M-UI button with built in:
 *    - failure and success snackbars
 *    - loading state
 *    - tooltips (disabled and/or not disabled)
 *    - easy error tracking
 *    - easy user activity tracking
 *    - easy testing with cypress
 *    - customizable styles
 * 
 * Props:
 *
 *  styleOverride - styles passed directly to M-UI button
 *  disabled - wether or not button is clickable
 *  variant - M-UI prop
 *  color - M-UI prop
 *  size - M-UI prop
 *  type - standard button prop: 'button', 'submit', etc.
 *  data-cy - like an aria label but specifically for Cypress testing
 *  children - children of the button
 *  onClick - onClick callback, called when button is clicked
 *  disabledDuringOnClick - wether or not the button should be disabled during onClick callback. if True a loading state will render inside button in leu of chilren
 *  loadingChildren - the children rendered during loading state. Defauls to M-UI circular progress
 *  onClickSuccessSnackbar - snackbar message which appears after onClick success
 *  onClickFailSnackbar - snackbar message which appears after onClick failure. if 'err' then the error's message is shown in the snackbar upon failure
 *  onClickValidation - validation callback, if provided and returns falsey value onClick callback will not fire when clicked
 * 
 */

const RenderButton = ({
  className,
  styleOverride,
  disabled,
  variant,
  color,
  children,
  onClick,
  size,
  type,
  dataCy,
  disabledDuringOnClick,
  loadingChildren,
  onClickSuccessSnackbar,
  onClickFailSnackbar,
  onClickValidation,
}, ref) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const handleClick = async (event) => {
    // put tracking here (mixpanel, amplitude, etc.)
    if (onClickValidation) {
      const validated = await onClickValidation(event);
      if (!validated) {
        console.error('Validation failed, inspect the onClickValidation')
        return
      }
    }
    if (onClick) {
      try {
        if (disabledDuringOnClick) setLoading(true);
        await onClick(event)
        if (onClickSuccessSnackbar) enqueueSnackbar(onClickSuccessSnackbar)
      } catch (err) {
        // error tracking here (Sentry, etc.)
        if (onClickFailSnackbar) {
          enqueueSnackbar(onClickFailSnackbar === 'err' ? err.message : onClickFailSnackbar);
        }
      } finally {
        if (disabledDuringOnClick) setLoading(false);
      }
    }
  }
  return (
    <Button
      ref={ref}
      type={type || 'button'}
      onClick={handleClick}
      variant={variant || 'text'}
      color={color || 'primary'}
      disabled={(disabled || (disabledDuringOnClick && loading) ||false)}
      size={size || 'medium'}
      data-cy={dataCy}
      style={styleOverride}
      className={className}
    >
      { loading ? (
        loadingChildren || (
          <>
            <div style={{ color: 'transparent', backgroundColor: 'transparent' }}>{children}</div>
            <CircularProgress size={20} style={{ position: 'absolute' }}/>
          </>
        )
      ) : (
      <div>
        {children}
      </div>
      )}
    </Button>
  )
}

// just an optional tooltip wrapper for the button itself, see "RenderButton" above
const CustomButton = forwardRef(({
  tooltip,
  disabledTooltip,
  ...rest
}, ref) => Boolean(rest.disabled ? disabledTooltip : tooltip) ? (
  <Tooltip
    title={rest.disabled ? disabledTooltip : tooltip}
  >
    <div>
      { RenderButton(rest, ref) }
    </div>
  </Tooltip>
) : RenderButton(rest, ref))

CustomButton.propTypes = {
  classes: PropTypes.object,
  styleOverride: PropTypes.object,
  disabled: PropTypes.bool,
  variant: PropTypes.string,
  type: PropTypes.string,
  color: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.string,
  ]),
  loadingChildren: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.string,
  ]),
  onClick: PropTypes.func,
  onClickValidation: PropTypes.func,
  size: PropTypes.string,
  tooltip: PropTypes.string,
  disabledTooltip: PropTypes.string,
  dataCy: PropTypes.string,
  disabledDuringOnClick: PropTypes.bool,
  onClickSuccessSnackbar: PropTypes.string,
  onClickFailSnackbar: PropTypes.string,
}

export default CustomButton
